import { Building2, Globe, Lock, UserPlus, Crown } from "lucide-react";
import { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Workspace } from '../../models/Workspace';
import { useAppUIContext } from '../../context/AppUIContext';
import { Fire } from '../../services/FireClient';
import { useHistory } from "../../hooks/useRouterCompat";
import { My } from '../../services/My';
import { useToast } from "../../components/ui/use-toast";
import { Progress } from "../../components/ui/progress";
import { Trans, t } from '@lingui/macro';

const WorkspaceMiniTitle: React.FC<{ workspace: Workspace }> = ({ workspace }) => {
  const { workspaces } = useAppUIContext();
  const { toast } = useToast();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);

  const isLocked = (workspace?.emailDomains?.length ?? 0) > 0;
  const isMember = useMemo(() => !!workspaces.find(w => w.id === workspace.id), [workspace.id, workspaces]);

  const joinWorkspace = async () => {
    if (!workspace) return;
    setInProgress(true);

    try {
      const res = await Fire.ws.joinWorkspace({ workspaceId: workspace.id, invitationKey: null });
      if (res === 'JOINED') {
        toast({ title: t`Joined`, description: t`You have joined the workspace ${workspace.name}` });
        history.replace(`/ws/${workspace.id}`);
      }
      else if (res === 'NOT_ALLOWED') {
        toast({ variant: "destructive", title: t`Error`, description: t`You are not allowed to join ${workspace.name}` });
      }
      else if (res === 'ALREADY_MEMBER') {
        toast({ title: t`Info`, description: t`You are already a member of ${workspace.name}` });
      } else {
        toast({ variant: "destructive", title: t`Error`, description: t`Unknown error, could not join workspace.` });
      }
    } catch (error) {
      toast({ variant: "destructive", title: t`Error`, description: `Error: ${(error as Error).message}` });
    } finally {
      setInProgress(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="h-auto p-1 px-2 text-xs text-muted-foreground hover:bg-orange-100 rounded-full flex items-center gap-1.5 transition-colors min-w-0"
          >
            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="font-medium truncate">{workspace.name}</span>
            {isLocked ? (
              <Lock className="w-3 h-3 opacity-70 text-orange-500 mt-0.5 flex-shrink-0" />
            ) : (
              <Globe className="w-3 h-3 opacity-70 text-orange-500 mt-0.5 flex-shrink-0" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96" align="start">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {workspace.name}
              </h4>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Crown className="w-4 h-4 text-amber-500" />
                <span>{workspace?.owner?.name}</span>
              </div>
            </div>
            <div className="grid gap-2">
              {isLocked ? (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 mt-0.5 text-orange-500" />
                    <div className="grid gap-1">
                      <span className="font-medium"><Trans>Restricted Access</Trans></span>
                      <span className="text-muted-foreground text-xs">
                        <Trans>Available to users with email domains:</Trans> {workspace.emailDomains.map(e => '@' + e).join(', ')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Globe className="w-4 h-4 mt-0.5 text-green-500" />
                    <div className="grid gap-1">
                      <span className="font-medium"><Trans>Open Workspace</Trans></span>
                      <span className="text-muted-foreground text-xs">
                        <Trans>This workspace can be joined by anyone.</Trans>
                      </span>
                    </div>
                  </div>
                  {!isMember && (
                    <Button
                      size="sm"
                      className="h-8 bg-green-600 hover:bg-green-700 text-white gap-1.5 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        joinWorkspace();
                      }}
                      disabled={inProgress}
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      <Trans>Join</Trans>
                    </Button>
                  )}
                </div>
              )}
            </div>
            {inProgress && <Progress value={undefined} className="h-1" />}
          </div>
        </PopoverContent>
      </Popover>


    </div>
  );
}

export default WorkspaceMiniTitle;
